import React, { useCallback, useContext, useState, useMemo } from 'react';
import { Avatar, Button, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { HeartOutlined, HeartFilled, MessageOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { goToApp } from "utils/helpers";
import { parseYouTubeLinks, createMediaItems } from 'utils/mediaUtils';
import { formatRelativeTime } from 'utils/DateUtils';
import { AppContext } from 'context/AppContext';
import ExpandableText from 'components/ExpandableText';
import MediaGrid from 'components/MediaGrid';
import './PostItem.css';

interface User {
  id?: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  profile?: {
    image_url?: string;
    image_small_url?: string;
    is_following?: boolean;
  };
}

interface PostItemProps {
  // Core content
  id: string;
  message: string;
  images?: any[];
  user?: User;
  created: string;
  
  // Engagement
  is_liked: boolean;
  like_count: number;
  comment_count: number;
  
  // Callbacks
  onLikeUpdate?: (updatedItem: any) => void;
  onCommentClick?: () => void;
  onUserClick?: (userId: string) => void;
  
  // API endpoints
  likeEndpoint?: string;
  unlikeEndpoint?: string;
  
  // Custom content slots
  headerExtra?: React.ReactNode;
  beforeContent?: React.ReactNode;
  afterContent?: React.ReactNode;
  afterActions?: React.ReactNode;
  
  // Options
  showFollow?: boolean;
  showCommentButton?: boolean;
  className?: string;
}

const PostItem: React.FC<PostItemProps> = ({
  id,
  message,
  images,
  user,
  created,
  is_liked,
  like_count,
  comment_count,
  onLikeUpdate,
  onCommentClick,
  onUserClick,
  likeEndpoint,
  unlikeEndpoint,
  headerExtra,
  beforeContent,
  afterContent,
  afterActions,
  showFollow = false,
  showCommentButton = true,
  className = ''
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authentication, api, commonData } = useContext(AppContext);
  const { authKey } = authentication;
  const { userDetails } = commonData;
  
  const [isLiked, setIsLiked] = useState(is_liked || false);
  const [likeCountState, setLikeCountState] = useState(like_count || 0);
  const [isFollowing, setIsFollowing] = useState(user?.profile?.is_following || false);
  const [followLoading, setFollowLoading] = useState(false);

  // Parse YouTube links from message
  const { cleanText, videoId } = useMemo(() => parseYouTubeLinks(message), [message]);

  // Create combined media array
  const mediaItems = useMemo(() => createMediaItems(videoId, images || []), [videoId, images]);

  const handleLike = useCallback(async () => {
    if (!authKey) {
      goToApp();
      return;
    }

    try {
      const newLikedState = !isLiked;
      const newLikeCount = newLikedState ? likeCountState + 1 : likeCountState - 1;
      
      setIsLiked(newLikedState);
      setLikeCountState(newLikeCount);

      if (isLiked) {
        await api.task.unlikeSubmission(id);
      } else {
        await api.task.likeSubmission(id);
      }
      
      if (onLikeUpdate) {
        onLikeUpdate({
          id,
          is_liked: newLikedState,
          like_count: newLikeCount
        });
      }
    } catch (error) {
      console.error('Error liking submission:', error);
      setIsLiked(!isLiked);
      setLikeCountState(likeCountState);
      notification.error({
        message: t('#Task.like_error'),
        description: t('#Task.like_error_description')
      });
    }
  }, [authKey, isLiked, likeCountState, id, onLikeUpdate, api.task, t]);

  const handleUserClick = useCallback(() => {
    if (user?.id) {
      if (onUserClick) {
        onUserClick(user.id);
      } else {
        navigate(`/profile/${user.id}`);
      }
    }
  }, [user?.id, navigate, onUserClick]);

  const handleFollow = useCallback(async () => {
    if (!authKey) {
      goToApp();
      return;
    }

    if (!user?.id) return;

    try {
      setFollowLoading(true);
      let isSuccess = false;
      
      if (isFollowing) {
        const { error = {} } = await api.profile.unfollowUser(user.id);
        isSuccess = !error || Object.keys(error).length === 0;
      } else {
        const { error = {} } = await api.profile.followUser(user.id);
        isSuccess = !error || Object.keys(error).length === 0;
      }
      
      if (isSuccess) {
        setIsFollowing(!isFollowing);
      }
    } catch (error) {
      console.error('Error following/unfollowing user:', error);
    } finally {
      setFollowLoading(false);
    }
  }, [authKey, isFollowing, user?.id, api.profile]);

  const handleCommentClick = useCallback(() => {
    if (!authKey) {
      goToApp();
      return;
    }
    if (onCommentClick) {
      onCommentClick();
    }
  }, [authKey, onCommentClick]);

  const formatDate = useCallback((dateString: string) => {
    return formatRelativeTime(dateString, t);
  }, [t]);

  return (
    <div className={`post-item ${className}`}>
      {/* User Info Header */}
      <div className="post-item-header">
        <Avatar
          src={user?.profile?.image_url || user?.profile?.image_small_url}
          size={40}
          className="user-avatar"
          onClick={handleUserClick}
        >
          {user?.first_name?.[0] || user?.username?.[0] || 'U'}
        </Avatar>
        <div className="user-info">
          <div className="user-name" onClick={handleUserClick}>
            {user?.first_name} {user?.last_name}
          </div>
          <div className="post-time">{formatDate(created)}</div>
        </div>
        
        {/* Header Extra Content */}
        {headerExtra}
        
        {/* Follow Button */}
        {showFollow && authKey && userDetails?.id !== user?.id && (
          <Button
            type={isFollowing ? 'primary' : 'default'}
            size="small"
            loading={followLoading}
            onClick={handleFollow}
            className="follow-button"
          >
            {isFollowing ? t('#Profile.following') : t('#Profile.follow')}
          </Button>
        )}
      </div>

      {/* Before Content Slot */}
      {beforeContent}

      {/* Media Grid */}
      {mediaItems.length > 0 && (
        <MediaGrid mediaItems={mediaItems} />
      )}

      {/* Content */}
      <div className="post-content">
        {cleanText && (
          <ExpandableText 
            text={cleanText} 
            maxLines={2} 
            maxLength={120}
            className="post-expandable-text"
          />
        )}
      </div>

      {/* After Content Slot */}
      {afterContent}

      {/* Actions */}
      <div className="post-actions">
        <Button
          type="text"
          icon={isLiked ? <HeartFilled style={{ color: '#ff4d4f' }} /> : <HeartOutlined />}
          onClick={handleLike}
          className="action-button"
        >
          {likeCountState > 0 && <span className="action-count">{likeCountState}</span>}
        </Button>
        
        {showCommentButton && (
          <Button
            type="text"
            icon={<MessageOutlined />}
            onClick={handleCommentClick}
            className="action-button"
          >
            {comment_count > 0 && <span className="action-count">{comment_count}</span>}
          </Button>
        )}
      </div>

      {/* After Actions Slot */}
      {afterActions}
    </div>
  );
};

export default PostItem;